import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { AgGridReact } from 'ag-grid-react';
import { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { enqueueSnackbar } from 'notistack';
import { Box, Button, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import axiosInstance from '../../utils/axios';
import EditIconButton from '../../reuseable-components/DefaultButtons/EditIconButton';
import DeleteIconButton from '../../reuseable-components/DefaultButtons/DeleteIconButton';
import UserEditForm from './UserEditForm';
import Iconify from '../../reuseable-components/iconify/Iconify';
import { UserContext } from '../../context/UserProvider';
import AgGridDefaults from '../../reuseable-components/AGGrid/AGGridDefaults';
import AGGridQuickFilterWrapper from '../../reuseable-components/AGGrid/AGGridQuickFilterWrapper';
import EditRoles from './EditRoles';

export default function UserList()
{
  const gridRef = useRef(null);
  const navigate = useNavigate();

  const { setUser } = useContext(UserContext);
  const [users, setUsers] = useState([]);
  const [editUser, setEditUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [openRoleForm, setOpenRoleForm] = useState(false);

  useEffect(() => {
    setLoading(true);
    axiosInstance.get("/user/users")
      .then(res => setUsers(res.data))
      .catch(() => enqueueSnackbar('User list failed', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, []);

  const deleteUser = (id) => {
    axiosInstance.post(`/user/deleteuser?id=${id}`)
      .then(() => {
        setUsers(users => [...users.filter(user => user.id !== id)]);
        enqueueSnackbar('User deleted', { variant: 'success' })
      })
      .catch(() => enqueueSnackbar('User delete failed', { variant: 'error' }))
  };

  const loginAsUser = (id) => {
    axiosInstance.get(`/user/loginAsUser?userId=${id}`)
      .then((res) => {
        if (res.data?.user) {
          setUser(res.data.user);
          navigate("/home");
          enqueueSnackbar(`Logged in as ${res.data.user.email}`, { variant: 'success' })
        } else enqueueSnackbar(`Log in failed`, { variant: 'error' })
      })
      .catch(() => enqueueSnackbar(`Log in failed`, { variant: 'error' }))
  };

  const columns = useMemo(() => [
    {
      field: 'firstName',
      headerName: 'First Name',
      minWidth: 150,
      flex: 1
    },
    {
      field: 'lastName',
      headerName: 'Last Name',
      minWidth: 150,
      flex: 1
    },
    {
      field: 'email',
      headerName: 'Contact Email',
      minWidth: 130,
      flex: 1
    },
    {
      field: 'actions',
      headerName: 'Actions',
      cellRenderer: (params) => <Stack direction="row" spacing={1}>
        <Tooltip title={"Modify roles"} arrow placement="top">
          <IconButton edge="end" color="secondary" disableRipple onClick={() => { setEditUser(params.data); setOpenRoleForm(true); }} sx={{ px: 0 }}>
            <SettingsIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={"Login as user"} arrow placement="top">
          <IconButton edge="end" color="secondary" disableRipple onClick={() => loginAsUser(params.data.id)} sx={{ px: 0 }}>
            <Iconify icon="ic:outline-log-in" />
          </IconButton>
        </Tooltip>
        <EditIconButton onClick={() => { setEditUser(params.data); setOpenForm(true); }} />
        <DeleteIconButton content={`Are you sure you want to delete ${params.data.firstName} (${params.data.email})?`} action={() => deleteUser(params.data.id)} />
      </Stack>
    }
  ], []);

  return (<>
    <Helmet>
      <title>User List | NHI Direct</title>
    </Helmet>

    <Box sx={{ height: "calc(100% - 55px)", mt: 2 }}>
      <AGGridQuickFilterWrapper gridRef={gridRef}>
        <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={() => { setEditUser(null); setOpenForm(true); }} size="small">
          <Typography>Add User</Typography>
        </Button>
      </AGGridQuickFilterWrapper> 
      {!loading ? <Box sx={{ height: "max(500px, 100%)" }}>
        <AgGridReact ref={gridRef} {...AgGridDefaults} columnDefs={columns} rowData={users} loading={loading} />
      </Box> : <Box sx={{ display: "flex", justifyContent: "center", m: 3 }}><CircularProgress /></Box>}
      <UserEditForm open={openForm} close={() => { setEditUser(null); setOpenForm(false); }} editUser={editUser} setEditUser={setEditUser} setUsers={setUsers} />
      <EditRoles open={openRoleForm} userId={editUser?.id} close={() => { setEditUser(null); setOpenRoleForm(false); }} />
    </Box>
  </>);
}
